import { cn } from "@/lib/utils";

export type CountTabTone =
  | "neutral"
  | "primary"
  | "accent"
  | "success"
  | "warning"
  | "error";

const DOT_CLASS: Record<CountTabTone, string> = {
  neutral: "bg-surface-400",
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  success: "bg-success-500",
  warning: "bg-warning-500",
  error: "bg-error-500",
};

const ACTIVE_COUNT_CLASS: Record<CountTabTone, string> = {
  neutral: "bg-surface-100 text-surface-700 ring-surface-500/15",
  primary: "bg-primary-50 text-primary-700 ring-primary-600/15",
  accent: "bg-accent-50 text-accent-700 ring-accent-600/15",
  success: "bg-success-50 text-success-700 ring-success-600/15",
  warning: "bg-warning-50 text-warning-700 ring-warning-600/15",
  error: "bg-error-50 text-error-700 ring-error-600/15",
};

/**
 * Label for a segmented status tab: optional status dot, text and a count pill.
 * Use as `ToggleGroup` option label so status tabs show their totals inline.
 */
export function CountTabLabel({
  label,
  count,
  tone = "neutral",
  active,
  showDot = tone !== "neutral",
}: {
  label: string;
  count: number | undefined;
  tone?: CountTabTone;
  active: boolean;
  showDot?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {showDot ? (
        <span
          className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT_CLASS[tone])}
          aria-hidden
        />
      ) : null}
      <span>{label}</span>
      {count !== undefined ? (
        <span
          className={cn(
            "min-w-5 rounded-md px-1.5 py-px text-center text-[11px] font-semibold tabular-nums ring-1 ring-inset transition-colors",
            active
              ? ACTIVE_COUNT_CLASS[tone]
              : "bg-surface-200/60 text-surface-500 ring-transparent dark:bg-surface-200/80",
          )}
        >
          {count}
        </span>
      ) : null}
    </span>
  );
}
